import { CounterpartyDetails } from "@common/schemas/counterpartyDetails.schema";
import {
    CounterpartyAddress,
    CreateCounterpartyAddressBody,
    UpdateCounterpartyAddressBody,
} from "@common/schemas/counterpartyAddress.schema";
import {
    CounterpartyContact,
    CreateCounterpartyContactBody,
    UpdateCounterpartyContactBody,
} from "@common/schemas/counterpartyContact.schema";
import {
    matchClonedAddress,
    matchClonedContact,
} from "./matchClonedCounterpartyChild";

export interface ContactReplacement {
    replacesId: string;
    body: CreateCounterpartyContactBody;
}

export interface AddressReplacement {
    replacesId: string;
    body: CreateCounterpartyAddressBody;
}

function contactUpdateBody(
    body: CreateCounterpartyContactBody,
): UpdateCounterpartyContactBody {
    return {
        label: body.label ?? null,
        title: body.title ?? null,
        firstName: body.firstName ?? null,
        middleName: body.middleName ?? null,
        lastName: body.lastName ?? null,
        suffix: body.suffix ?? null,
        email: body.email ?? null,
        phoneNumber: body.phoneNumber ?? null,
    };
}

function addressUpdateBody(
    body: CreateCounterpartyAddressBody,
): UpdateCounterpartyAddressBody {
    return {
        label: body.label ?? null,
        street: body.street ?? null,
        street2: body.street2 ?? null,
        city: body.city ?? null,
        postalCode: body.postalCode ?? null,
        state: body.state ?? null,
        country: body.country ?? null,
    };
}

export function placeCounterpartyReplacementRows<T extends { id: string }>({
    saved,
    seeds,
    replacesIds,
}: {
    saved: T[];
    seeds: T[];
    replacesIds: ReadonlyArray<string | null>;
}): T[] {
    const replacementByTarget = new Map<string, T>();
    const extras: T[] = [];
    seeds.forEach((seed, index) => {
        const replacesId = replacesIds[index] ?? null;
        if (replacesId) {
            replacementByTarget.set(replacesId, seed);
            return;
        }
        extras.push(seed);
    });
    const placed = saved.flatMap((row) => {
        const replacement = replacementByTarget.get(row.id);
        return replacement ? [row, replacement] : [row];
    });
    const placedIds = new Set(placed.map((row) => row.id));
    const unmatched = seeds.filter(
        (seed, index) => Boolean(replacesIds[index]) && !placedIds.has(seed.id),
    );
    return [...placed, ...unmatched, ...extras];
}

export function displayedRemovedIds<T extends { id: string }>({
    rows,
    sourceRows,
    removedIds,
    isClone,
}: {
    rows: T[];
    sourceRows: T[];
    removedIds: readonly string[];
    isClone: (source: T, candidate: T) => boolean;
}): string[] {
    const removed = new Set(removedIds);
    return rows.flatMap((row) => {
        if (removed.has(row.id)) {
            return [row.id];
        }
        const isRemovedClone = sourceRows.some(
            (sourceRow) => removed.has(sourceRow.id) && isClone(sourceRow, row),
        );
        return isRemovedClone ? [row.id] : [];
    });
}

async function replacedContactId({
    replacement,
    sourceContacts,
    counterpartyId,
    sourceCounterpartyId,
    writableContacts,
}: {
    replacement: ContactReplacement;
    sourceContacts: CounterpartyContact[];
    counterpartyId: string;
    sourceCounterpartyId: string;
    writableContacts: CounterpartyContact[];
}): Promise<string> {
    const source = sourceContacts.find(
        (contact) => contact.id === replacement.replacesId,
    );
    if (!source) {
        throw new Error("Missing replaced contact");
    }
    if (counterpartyId === sourceCounterpartyId) {
        return source.id;
    }
    const clone = matchClonedContact(source, writableContacts);
    if (!clone) {
        throw new Error("Missing replaced contact");
    }
    return clone.id;
}

async function replacedAddressId({
    replacement,
    sourceAddresses,
    counterpartyId,
    sourceCounterpartyId,
    writableAddresses,
}: {
    replacement: AddressReplacement;
    sourceAddresses: CounterpartyAddress[];
    counterpartyId: string;
    sourceCounterpartyId: string;
    writableAddresses: CounterpartyAddress[];
}): Promise<string> {
    const source = sourceAddresses.find(
        (address) => address.id === replacement.replacesId,
    );
    if (!source) {
        throw new Error("Missing replaced address");
    }
    if (counterpartyId === sourceCounterpartyId) {
        return source.id;
    }
    const clone = matchClonedAddress(source, writableAddresses);
    if (!clone) {
        throw new Error("Missing replaced address");
    }
    return clone.id;
}

export async function persistCounterpartyReplacements({
    counterpartyId,
    sourceCounterpartyId,
    contacts,
    addresses,
    contactReplacements,
    addressReplacements,
    fetchDetails,
    updateContact,
    updateAddress,
}: {
    counterpartyId: string;
    sourceCounterpartyId: string;
    contacts: CounterpartyContact[];
    addresses: CounterpartyAddress[];
    contactReplacements: ContactReplacement[];
    addressReplacements: AddressReplacement[];
    fetchDetails: (
        id: string,
    ) => Promise<Pick<CounterpartyDetails, "contacts" | "addresses">>;
    updateContact: (input: {
        counterpartyId: string;
        contactId: string;
        body: UpdateCounterpartyContactBody;
    }) => Promise<CounterpartyContact>;
    updateAddress: (input: {
        counterpartyId: string;
        addressId: string;
        body: UpdateCounterpartyAddressBody;
    }) => Promise<CounterpartyAddress>;
}): Promise<boolean> {
    if (contactReplacements.length === 0 && addressReplacements.length === 0) {
        return false;
    }

    try {
        const writable =
            counterpartyId === sourceCounterpartyId
                ? { contacts, addresses }
                : await fetchDetails(counterpartyId);
        const results = await Promise.allSettled([
            ...contactReplacements.map(async (replacement) => {
                const contactId = await replacedContactId({
                    replacement,
                    sourceContacts: contacts,
                    counterpartyId,
                    sourceCounterpartyId,
                    writableContacts: writable.contacts,
                });
                return await updateContact({
                    counterpartyId,
                    contactId,
                    body: contactUpdateBody(replacement.body),
                });
            }),
            ...addressReplacements.map(async (replacement) => {
                const addressId = await replacedAddressId({
                    replacement,
                    sourceAddresses: addresses,
                    counterpartyId,
                    sourceCounterpartyId,
                    writableAddresses: writable.addresses,
                });
                return await updateAddress({
                    counterpartyId,
                    addressId,
                    body: addressUpdateBody(replacement.body),
                });
            }),
        ]);
        return results.some((result) => result.status === "rejected");
    } catch {
        return true;
    }
}
